import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { AppTextColor } from '@shared/ui/typography'
import type { DisciplineBalance } from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useListsStore } from '@shared/model'
import { resolveUrl } from '@shared/lib/url'
import { useDayjs } from '@shared/lib/format'
import { AppRoute } from '@shared/config'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'
import { ROWS_PER_PAGE_OPTIONS } from '@shared/ui/paginations'

import {
  BALANCE_EMPTY_CELL,
  formatBalanceCounter,
  getBalanceCommentPreview,
  getBalanceReferenceTitle,
} from './discipline-balances'
import { useDisciplineStore } from './store'
import { useDisciplineBalancesStore } from './balances/store'
import { DisciplineBalanceReferenceType } from './enums'

interface BalanceCellLink {
  text: string
  url: string | null
}

interface DisciplineBalanceRow {
  id: number
  createdAt: string
  counter: string
  counterColor: AppTextColor
  balance: number
  reason: string
  responsible: BalanceCellLink
  reference: BalanceCellLink
  comment: string
  commentTooltip: string | null
}

interface TableDisciplineBalancesReturn {
  rows: ComputedRef<DisciplineBalanceRow[]>
  isLoading: ComputedRef<boolean>
  hasBalances: ComputedRef<boolean>
  meta: ComputedRef<Meta>
  perPageOptions: ComputedRef<number[]>
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
}

export const useTableDisciplineBalances = (): TableDisciplineBalancesReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useDisciplineBalancesStore()
  const disciplineStore = useDisciplineStore()
  const listsStore = useListsStore()
  const dayjs = useDayjs()

  const isLoading = computed<boolean>(() => store.isLoading)
  const hasBalances = computed<boolean>(() => store.hasBalances)
  const meta = computed<Meta>(() => store.getMeta ?? DEFAULT_APP_TABLE_META)

  const perPageOptions = computed<number[]>(() => {
    const current = meta.value.per_page
    if (!current || ROWS_PER_PAGE_OPTIONS.includes(current)) return ROWS_PER_PAGE_OPTIONS

    return [...ROWS_PER_PAGE_OPTIONS, current].sort((a, b) => a - b)
  })

  const getResponsible = (balance: DisciplineBalance): BalanceCellLink => {
    const responsible = balance.responsible
    if (!responsible?.name) return { text: 'System', url: null }

    return { text: responsible.name, url: resolveUrl(AppRoute.UserEdit, { id: responsible.id }) }
  }

  /** The API returns a typed reference; a lesson opens its own page, where the legacy row opened it on the Lessons tab. */
  const getReferenceUrl = (balance: DisciplineBalance): string | null => {
    const reference = balance.reference
    if (!reference) return null

    if (reference.type === DisciplineBalanceReferenceType.Lesson) {
      return resolveUrl(AppRoute.LessonsEdit, { id: route.params.id as string, lessonId: reference.id })
    }

    if (reference.type === DisciplineBalanceReferenceType.Subscription) {
      const customerId = disciplineStore.getCustomerId
      return customerId ? resolveUrl(AppRoute.CustomersSubscriptions, { id: customerId }) : null
    }

    return resolveUrl(AppRoute.OrdersShow, { id: reference.id })
  }

  const getReference = (balance: DisciplineBalance): BalanceCellLink => (balance.reference
    ? { text: getBalanceReferenceTitle(balance.reference), url: getReferenceUrl(balance) }
    : { text: BALANCE_EMPTY_CELL, url: null })

  const rows = computed<DisciplineBalanceRow[]>(() => {
    const reasonLabels = listsStore.getLists?.balance.reason ?? {}

    return store.getBalances.map((balance) => {
      const comment = getBalanceCommentPreview(balance.comment)

      return {
        id: balance.id,
        createdAt: dayjs(balance.created_at).format(DateFormat.KanbanDate),
        counter: formatBalanceCounter(balance.value),
        counterColor: balance.value > 0 ? 'emerald-600' : 'red-600',
        balance: balance.balance,
        reason: reasonLabels[balance.reason] || balance.reason || '-',
        responsible: getResponsible(balance),
        reference: getReference(balance),
        comment: comment.text,
        commentTooltip: comment.tooltip,
      }
    })
  })

  const setChangePage = async (value: number): Promise<void> => {
    store.setCurrentPage(value)
    await router.push({
      query: {
        ...route.query,
        page: String(value),
      },
    })
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    store.setPerPage(value)
    store.setCurrentPage(1)
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
        page: undefined,
      },
    })
  }

  return {
    rows,
    isLoading,
    hasBalances,
    meta,
    perPageOptions,
    setChangePage,
    setChangePerPage,
  }
}
